import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Snackbar,
    Alert,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import { themeColors } from '../../theme/colors';

interface ShareButtonProps {
    disabled?: boolean;
}

const ShareButton: React.FC<ShareButtonProps> = ({ disabled = false }) => {
    const [open, setOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const shareUrl = window.location.href;
    const shareTitle = 'Результаты поиска товаров - Скайнет';
    const shareText = 'Посмотрите эти товары в прайс-листе Скайнет';

    const handleShare = async () => {
        // Проверяем поддержку Web Share API (обычно на мобильных)
        if (navigator.share && isMobile) {
            try {
                await navigator.share({
                    title: shareTitle,
                    text: shareText,
                    url: shareUrl,
                });
            } catch (error) {
                console.log('Sharing cancelled or failed:', error);
            }
        } else {
            // Открываем модалку на десктопе
            setOpen(true);
        }
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setSnackbarMessage('Ссылка скопирована в буфер обмена!');
            setSnackbarOpen(true);
            setOpen(false);
        } catch (error) {
            console.error('Failed to copy:', error);
            setSnackbarMessage('Ошибка при копировании ссылки');
            setSnackbarOpen(true);
        }
    };

    const shareViaEmail = () => {
        const subject = encodeURIComponent(shareTitle);
        const body = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    };

    const shareViaWhatsApp = () => {
        const text = encodeURIComponent(`${shareText} ${shareUrl}`);
        window.open(`https://wa.me/?text=${text}`, '_blank');
    };

    const shareViaTelegram = () => {
        const text = encodeURIComponent(`${shareText} ${shareUrl}`);
        window.open(`https://t.me/share/url?url=${shareUrl}&text=${text}`, '_blank');
    };

    return (
        <>
            <IconButton
                onClick={handleShare}
                disabled={disabled}
                sx={{
                    borderColor: themeColors['300'],
                    color: themeColors['600'],
                    '&:hover': {
                        borderColor: themeColors['400'],
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                }}
            >
                <ShareIcon />
            </IconButton>

            {/* Модалка для десктопа */}
            <Dialog
                sx={{
                    "&>.MuiDialog-container": {
                        "&>.MuiPaper-root": {
                            background: '#fff'
                        }
                    }
                }}
                open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1
                }}>

                    <IconButton onClick={() => setOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Скопируйте ссылку или поделитесь через доступные сервисы
                    </Typography>

                    <Box sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        mb: 3,
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 1 }}>
                            {shareUrl}
                        </Typography>
                        <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={copyToClipboard}
                            variant="outlined"
                        >
                            Копировать ссылку
                        </Button>
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Поделиться через:
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            startIcon={<EmailIcon />}
                            onClick={shareViaEmail}
                            size="small"
                        >
                            Email
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={shareViaWhatsApp}
                            size="small"
                            sx={{ color: '#25D366', borderColor: '#25D366' }}
                        >
                            WhatsApp
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={shareViaTelegram}
                            size="small"
                            sx={{ color: '#0088cc', borderColor: '#0088cc' }}
                        >
                            Telegram
                        </Button>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar для уведомлений */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="success">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ShareButton;
